<template>
  <div
    class="f-flex f-flex-wrap f-justify-between"
    :class="
      !last ? `fapi-product-${item.code} f-mb-4` : `fapi-product-${item.code}`
    "
    v-if="isVisible"
  >
    <div
      :class="[
        item.allowChangeCounts || item.showStockInformation
          ? 'f-max-w-1/2'
          : 'f-max-w-3/4',
      ]"
    >
      <BaseRadio
        :id="$id(item.id + '-item')"
        :name="item.groupId + '_item_group_' + item.id"
        :value="true"
        :label="item.name"
        v-if="renderRadio"
        v-model="selected"
        @input="itemSelected"
      >
        <span
          class="f-inline"
          v-bind:class="{ 'f-line-through': item.soldOut }"
          :aria-label="`${item.name}, cena: ${formatFinalPrice()}`"
        >
          {{ item.name }}
        </span>
        <ItemImage :item="item"></ItemImage>
      </BaseRadio>
      <BaseCheckbox
        :id="$id(item.id + '-item')"
        :disabled="disabled"
        :value="true"
        v-else
        v-model="selected"
        @input="itemSelected"
      >
        <span
          class="f-inline"
          :class="{ 'f-line-through': item.soldOut }"
          :aria-label="`${item.name}, cena: ${formatFinalPrice()}`"
        >
          {{ item.name }}
        </span>
        <ItemImage :item="item"></ItemImage>
      </BaseCheckbox>
    </div>
    <div
      :class="[
        'f-flex',
        'f-flex-col-reverse',
        'sm:f-flex-row',
        'f-self-start',
        'f-pl-1',
        item.allowChangeCounts || item.showStockInformation
          ? 'f-max-w-1/2'
          : 'f-max-w-1/4',
      ]"
    >
      <div class="f-col-auto f-min-w-min f-mt-1 sm:f-mt-0">
        <ValidationProvider
          ref="countValidator"
          slim
          tag="div"
          :rules="{ ...rules }"
          v-slot="{ errors }"
          v-if="item.allowChangeCounts || item.showStockInformation"
        >
          <div
            v-if="item.allowChangeCounts"
            class="f-flex f-items-center f-space-x-1 sm:f-space-x-2 f-justify-end"
          >
            <MinusIcon @click="onDecrement" />
            <input
              class="f-form-input f-w-14 f-h-7 f-border-gray-300 f-rounded f-px-1 f-text-center f-text-gray-700"
              style="-moz-appearance: textfield"
              type="number"
              :disabled="item.soldOut"
              :max="item.enableStock ? item.stock : null"
              :min="0"
              v-model="count"
              @input="itemCountChanged($event.target.value)"
            />
            <PlusIcon @click="onIncrement" />
          </div>
          <template v-if="item.showStockInformation">
            <div class="f-text-sm f-text-red-600" v-if="item.stock > 0">
              {{ $tc("items.item.stockInfo", Math.floor(item.stock)) }}
            </div>
            <div class="f-text-sm f-text-red-600" v-if="item.stock === 0">
              {{ $t("items.item.noStock") }}
            </div>
          </template>
          <div v-if="errors.length" class="f-text-xs f-text-red-600">
            {{ errors[0] }}
          </div>
        </ValidationProvider>
      </div>
      <div
        v-on:mouseover="priceHover = true"
        v-on:mouseleave="priceHover = false"
        class="f-col-auto f-text-right f-relative sm:f-w-32"
      >
        <div
          v-show="priceHover"
          class="f-bg-white f-border f-mr-28 f--mt-4 f-absolute f-top-0 f-right-0 f-p-2 f-rounded f-text-gray-700 f-z-10"
        >
          <div
            class="f-w-4 f-h-4 f-border-t f-border-r f-bg-white f-absolute f-top-0 f-right-0 f-rounded-r f-rotate-45"
            style="margin: 18px -9px 0 0; transform: rotate(45deg) !important"
          ></div>
          <div class="f-relative f-text-sm">
            <div class="f-flex f-justify-between">
              <div class="f-min-w-max f-mr-6 f-text-left">
                {{ $t("item.prices.tooltip.base") }}
              </div>
              <div>
                {{
                  $n(
                    item.currentDiscountedPrice || item.currentPrice,
                    "currency",
                  )
                }}
              </div>
            </div>

            <div class="f-flex f-justify-between">
              <div class="f-min-w-max f-mr-6 f-text-left">
                {{ $n(item.currentVat) }} %
                {{ $t("item.prices.tooltip.vat") }}
              </div>
              <div>
                {{
                  $n(
                    item.currentDiscountedPriceIncVat -
                      item.currentDiscountedPrice ||
                      item.currentPriceIncVat - item.currentPrice,
                    "currency",
                  )
                }}
              </div>
            </div>

            <div class="f-flex f-justify-between">
              <div class="f-min-w-max f-mr-6 f-text-left">
                {{ $t("item.prices.tooltip.includingVat") }}
              </div>
              <div>
                {{
                  $n(
                    item.currentDiscountedPriceIncVat ||
                      item.currentPriceIncVat,
                    "currency",
                  )
                }}
              </div>
            </div>
          </div>
        </div>
        <div>
          <div
            v-if="item.currentDiscountedPrice !== null || item.isSpecialPrice"
            class="f-line-through fapi-form-item-price"
          >
            {{ $n(formatOriginPrice(), "currency") }}
            <br />
          </div>

          <div
            v-bind:class="{
              'fapi-form-discounted-price':
                item.currentDiscountedPrice !== null || item.isSpecialPrice,
              'fapi-form-item-price': true,
            }"
          >
            {{ formatFinalPrice() }}
          </div>

          <div v-if="period !== null" class="f-text-xs f-text-gray-400">
            {{ period }}
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="item.publicDescription"
      class="f-w-full f-text-sm f-text-gray-600 f-mt-1"
      v-html="item.publicDescription"
    ></div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import FormItem from "@/module/FormItem";
import { ValidationProvider } from "vee-validate";
import FormItemGroup from "@/module/FormItemGroup";
import FormResource from "@/module/Form/Resources/FormResource";
import {
  isNumberOrNull,
  isStringOrNull,
} from "@/module/ComponentPropsValidations/validations";
import BaseRadio from "@/components/Form/Basic/Inputs/BaseRadio.vue";
import MinusIcon from "@/components/Form/Basic/Inputs/MinusIcon.vue";
import PlusIcon from "@/components/Form/Basic/Inputs/PlusIcon.vue";
import BaseCheckbox from "@/components/Form/Basic/Inputs/BaseCheckbox.vue";
import money from "@/module/Filters/MoneyFilter";
import UrlParser from "@/module/Utils/UrlParser";
import ItemImage from "@/components/Form/Items/ItemImage.vue";

interface ComponentData {
  selected: boolean;
  count: number;
  priceHover: boolean;
}

export default Vue.extend({
  name: "Item",

  components: {
    ItemImage,
    BaseCheckbox,
    PlusIcon,
    MinusIcon,
    BaseRadio,
    ValidationProvider,
  },

  props: {
    item: {
      type: Object as PropType<FormItem>,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    last: {
      type: Boolean,
      required: true,
    },
    isFirst: {
      type: Boolean,
      required: true,
    },
    itemGroup: {
      type: Object as PropType<FormItemGroup>,
      required: true,
    },
    showPricesIncludingVat: {
      type: Boolean,
      required: true,
    },
    renderLikeRadio: {
      type: Boolean,
      required: true,
    },
    isVatPayer: {
      type: Boolean,
      required: true,
    },
    selectedPeriod: {
      type: null as unknown as PropType<string | null>,
      required: true,
      validator: isStringOrNull,
    },
    selectedRadioId: {
      required: true,
      validator: isNumberOrNull,
    },
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
  },

  data(): ComponentData {
    return {
      selected: false,
      count: 1,
      priceHover: false,
    };
  },

  computed: {
    rules(): any {
      const rules = {
        min_value: 0,
        max_value: null,
      } as any;
      if (!this.selected) {
        return {};
      }

      let maxCounts = [] as Array<number>;

      rules.min_value = this.item.metadata?.min_count || rules.min_value;

      if (this.item.enableStock ? this.item.stock : null) {
        maxCounts.push(Number(this.item.stock));
      }

      if (this.item.metadata?.max_count) {
        maxCounts.push(Number(this.item.metadata.max_count));
      }

      if (maxCounts.length > 0) {
        rules.max_value = Math.min(...maxCounts);
      }

      if (rules.max_value === null) {
        delete rules.max_value;
      }

      return rules;
    },

    renderRadio(): boolean {
      return !this.disabled && this.renderLikeRadio && this.itemGroup.required;
    },

    disabled: function (): boolean {
      return this.item.required || this.item.soldOut;
    },

    isVisible(): boolean {
      if (this.selectedPeriod === null || this.item.period === null) {
        return true;
      }

      return this.item.period === this.selectedPeriod;
    },

    period(): string | null {
      if (this.selectedPeriod === null) {
        return null;
      }

      let changeableItem = this.formResource.changeable_items.find((item) => {
        return item.base_item_template_id === this.item.itemTemplateId;
      });

      if (
        this.formResource.allow_changeable_items &&
        changeableItem !== undefined
      ) {
        return null;
      }

      return this.$tc(
        "items.periods." + this.selectedPeriod.split(" ")[1],
        Number(this.selectedPeriod.split(" ")[0]),
      ).toString();
    },
  },

  methods: {
    formatFinalPrice(): string {
      let price = 0.0;

      if (this.item.currentDiscountedPrice !== null) {
        price = this.formatDiscountedPrice();
      } else if (this.item.isSpecialPrice) {
        price = this.formatSpecialPrice();
      } else {
        price = this.formatOriginPrice();
      }

      return money(price, this.$root.$i18n);
    },

    formatOriginPrice(): number {
      let price = 0.0;
      if (this.item.isSpecialPrice) {
        price = this.showPricesIncludingVat
          ? Number(this.item.originalPriceIncVat)
          : Number(this.item.originalPrice);
      } else {
        price = this.showPricesIncludingVat
          ? Number(this.item.currentPriceIncVat)
          : Number(this.item.currentPrice);
      }

      return price;
    },

    formatDiscountedPrice(): number {
      return this.showPricesIncludingVat
        ? Number(this.item.currentDiscountedPriceIncVat)
        : Number(this.item.currentDiscountedPrice);
    },

    formatSpecialPrice(): number {
      return this.showPricesIncludingVat
        ? Number(this.item.currentPriceIncVat)
        : Number(this.item.currentPrice);
    },

    itemSelected(): void {
      if (this.renderRadio) {
        this.selected = true;
      }

      if (this.count === 0 && this.selected) {
        this.count = 1;
      }

      this.emitItemDataChanged();
    },

    itemCountChanged(value: number): void {
      let count = Number(value);

      if (this.item.enableStock) {
        if (this.item.stock !== null && count > this.item.stock) {
          count = this.item.stock;
        }

        if (this.item.stock === 0) {
          count = 0;
        }
      }

      if (count <= 0) {
        count = 0;
      }

      if (this.item.required && count <= 0) {
        count = 1;
      }

      this.selected = count !== 0;

      if (this.count === count) {
        return;
      }

      this.count = count;

      (
        this.$refs.countValidator as InstanceType<typeof ValidationProvider>
      )?.validate();

      this.emitItemDataChanged();
    },

    onIncrement(): void {
      if (this.item.soldOut) {
        return;
      }

      let count = this.count;
      this.itemCountChanged(++count);
    },

    onDecrement(): void {
      if (this.item.soldOut) {
        return;
      }

      if (this.count <= 0) {
        return;
      }

      let count = this.count;
      this.itemCountChanged(--count);
    },

    emitItemDataChanged(): void {
      const currentCount = Number(this.count);

      if (currentCount === 0) {
        this.selected = false;
      }

      if (!this.selected) {
        this.count = 0;
      }

      this.$emit("itemDataChanged", {
        id: this.item.id,
        selected: this.selected,
        count: this.count,
      });

      if (this.renderLikeRadio && this.selected) {
        this.$emit("selectRadio", { id: this.item.id });
      }
    },
  },

  watch: {
    isVisible(isVisible: boolean) {
      if (isVisible) {
        if (!this.selected && this.item.required) {
          this.selected = true;
        }
      } else {
        this.selected = false;
      }

      this.$nextTick(this.emitItemDataChanged);
    },

    selectedRadioId(): void {
      if (!this.renderLikeRadio) {
        return;
      }

      if (!this.selectedRadioId) {
        return;
      }

      this.selected = this.selectedRadioId === this.item.id;
      this.emitItemDataChanged();
    },
  },

  mounted(): void {
    this.selected = this.item.required;

    if (
      this.isFirst &&
      !Boolean(this.formResource.form_settings?.disable_autofill_product) &&
      this.itemGroup.required
    ) {
      this.selected = true;
    }

    const urlParser = UrlParser.getInstance();
    const value =
      urlParser.getParamFromFormData("fapi-form-item-" + this.item.code) ||
      urlParser.getParam("fapi-form-item-" + this.item.code);

    if (value !== null && value !== "") {
      this.selected = value !== "0";
    }

    const countParam =
      urlParser.getParamFromFormData(
        "fapi-form-item-count-" + this.item.code,
      ) || urlParser.getParam("fapi-form-item-count-" + this.item.code);

    let count = 1;

    if (countParam !== null && countParam !== "" && Number(countParam) > 0) {
      count = Number(countParam);
    }

    if (this.item.enableStock) {
      if (this.item.stock === 0) {
        this.selected = false;
      }
    }

    this.count = this.selected ? count : 0;

    if (this.selected) {
      this.$nextTick(this.emitItemDataChanged);
    }
  },
});
</script>
<style lang="scss">
.fapi-order-form {
  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.f-max-w-1\/4 {
  max-width: 25%;
}

.f-max-w-1\/2 {
  max-width: 50%;
}

.f-max-w-3\/4 {
  max-width: 75%;
}
</style>
